import { Box, Button, Chip, Dialog, DialogActions, DialogContent, TextField, Typography } from "@mui/material";
import BusinessImage from "assets/images/business.png";
import { Field, Form, Formik } from "formik";
import { awsImageRootUrl } from "lib/constants";
import { CreateBusiness } from "lib/features/auth/types";
import { useUpadateBusinessMutation } from "lib/features/business/businessService";
import { Business } from "lib/features/business/types";
import { Category, fetchCategory } from "lib/features/category/categoryService";
import { useGetIndustryTypesQuery } from "lib/features/industries/industriesService";
import { Industry } from "lib/features/industries/types";
import { useUploadImageMutation } from "lib/features/s3/s3Service";
import { UploadImageRequest } from "lib/features/s3/types";
import { User } from "lib/features/user/types";
import { selectUser } from "lib/features/user/userSlice";
import { useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { useSelector } from "react-redux";
import authServices from "services/auth-service";
import { editBusinessProfileStyles } from "styles/BusinessDirectory/editBusinessProfile.styles";
import { getCroppedImage } from "util/cropImage";
import { v4 as uuid } from "uuid";
import Label from "../NewItemForm/components/Label";
import PostScript from "../NewItemForm/components/PostScript";
import { editBusinessValidationSchema } from "./editBusiness.schema";

type EditBusinessFormProps = {
  businessDetails: Business;
  refetch: any;
  setIsEditing: React.Dispatch<React.SetStateAction<boolean>>;
};

const EditBusinessForm = ({
  businessDetails,
  refetch,
  setIsEditing,
}: EditBusinessFormProps) => {
  const user = useSelector(selectUser);
  const { data: industries } = useGetIndustryTypesQuery(null, {});
  const [uploadImage] = useUploadImageMutation();
  const [upadateBusiness, { isLoading: editLoading }] =
    useUpadateBusinessMutation();

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [userData, setUserData] = useState<User>();
  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [selectedIndustry, setSelectedIndustry] = useState<number>(
    businessDetails?.industries[0]?.id
  );

  const [openCropper, setOpenCropper] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);

  const onCropComplete = (croppedArea: any, croppedAreaPixels: any) => {
    setCroppedAreaPixels(croppedAreaPixels);
  };

  const handleCropConfirm = async (setFieldValue: any) => {
    if (!imageSrc || !croppedAreaPixels) return;
    try {
      const croppedBase64 = await getCroppedImage(imageSrc, croppedAreaPixels);
      if (croppedBase64) {
        const response = await resizeAndUploadImage(croppedBase64);
        if (response) {
          const imageUrl = `${response}`;
          setFieldValue("image", imageUrl);
        }
      }
    } catch (err) {
      console.error("Crop/Upload failed:", err);
    } finally {
      setOpenCropper(false);
    }
  };

  const initialValues: CreateBusiness = {
    user_id: businessDetails?.business_users?.[0]?.id ?? 0,
    business: businessDetails?.business,
    business_headline: businessDetails?.business_headline ?? "",
    business_description: businessDetails?.business_description,
    address1: businessDetails?.address1,
    address2: businessDetails?.address2,
    city: businessDetails?.city,
    state: businessDetails?.state,
    zipcode: businessDetails?.zipcode,
    phone: businessDetails?.phone,
    timezone: businessDetails?.timezone,
    default_commission: businessDetails?.default_commission,
    stripe_id: businessDetails?.stripe_id,
    farehabor_shortname: businessDetails?.farehabor_shortname,
    industry_ids: businessDetails?.industries?.flatMap((i) => i.id) ?? [],
    category_ids: businessDetails?.categories?.flatMap((i) => i.id) ?? [],
    region_id: Number(businessDetails?.region?.id),
    image: businessDetails?.businessImages?.[0]?.uri,
  };

  const resizeAndUploadImage = async (image: string) => {
    return new Promise((resolve, reject) => {
      const id = uuid() + ".jpg";
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext("2d");

      if (ctx && canvas) {
        const img = new Image();
        img.onload = async () => {
          const targetWidth = 1080;
          const scaleFactor = targetWidth / img.width;
          const targetHeight = img.height * scaleFactor;

          canvas.width = targetWidth;
          canvas.height = targetHeight;

          ctx.clearRect(0, 0, targetWidth, targetHeight);
          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

          // Convert canvas image to base64
          const base64String = canvas.toDataURL("image/jpeg").split(",")[1];
          const fileSize = atob(base64String).length;

          const payload: UploadImageRequest = {
            id,
            image: {
              base64: base64String,
              width: targetWidth,
              height: targetHeight,
              fileSize,
              uri: "",
              type: "image/jpeg",
              fileName: id,
            },
          };

          try {
            const result = await uploadImage(payload);
            resolve(result?.data);
          } catch (error) {
            console.log("Upload error:", error);
            reject(error);
          }
        };

        img.onerror = (err) => reject("Error loading image.");
        img.src = image;
      } else {
        reject("Canvas context is not available.");
      }
    });
  };

  const onSubmit = async (values: CreateBusiness) => {
    console.log(values, "values");
    const body = {
      ...values,
      region_id: Number(userData?.regions?.[0]?.region?.id),
    };
    console.log(body, "body");
    await upadateBusiness({
      business_id: businessDetails?.id,
      payload: body,
    });
    refetch();
    setIsEditing(false);
  };

  useEffect(() => {
    const getCategories = async () => {
      try {
        const categories = await fetchCategory();
        setCategories(categories);
      } catch (error) {}
    };

    getCategories();
  }, []);

  useEffect(() => {
    if (selectedIndustry && categories) {
      let newCategories = categories?.filter(
        (category: Category) =>
          category?.category !== "All" &&
          (category?.category_type === selectedIndustry ||
            category?.category_type === 0)
      );
      setFilteredCategories(newCategories);
    }
  }, [categories, selectedIndustry]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (user?.id) {
        try {
          const response = await authServices.fetchUser(user?.id);
          setUserData(response?.data);
        } catch (error: any) {
          console.error("Error fetching chats:", error?.message);
        }
      }
    };

    fetchUserDetails();
  }, [user?.id]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={editBusinessValidationSchema}
      enableReinitialize={true}
      onSubmit={(values) => onSubmit(values)}
    >
      {({ values, errors, touched, setFieldValue, handleChange }) => {
        
        const getCategoriesForIndustry = (industryId: number) => {
          return categories.filter(
            (category: Category) =>
              category?.category !== "All" &&
              (category?.category_type === industryId || category?.category_type === 0)
          ).map(cat => cat.id);
        };

        const handleSelectIndustryCategory = (industry_id: number, setFieldValue: any) => {
          // Simply switch to the new industry without clearing any categories
          setSelectedIndustry(industry_id);
          
          // Add the new industry to industry_ids if not already present
          if (!values.industry_ids?.includes(industry_id)) {
            setFieldValue("industry_ids", [
              ...(values.industry_ids ?? []),
              industry_id,
            ]);
          }
        };

        const handleSelectCategory = (category_id: number) => {
          const isCategorySelected = values?.category_ids?.includes(category_id);
          const currentCategoryIds = values?.category_ids ?? [];

          if (isCategorySelected) {
            // Remove the category
            const newCategoryIds = currentCategoryIds.filter(
              (id) => id !== category_id
            );
            setFieldValue("category_ids", newCategoryIds);
          } else {
            // Add the category
            const categoryToAdd = filteredCategories.find(cat => cat.id === category_id);
            if (!categoryToAdd) return;

            // Get all categories that belong to the current selected industry
            const categoriesFromCurrentIndustry = getCategoriesForIndustry(selectedIndustry);
            
            // Get currently selected categories that belong to this industry
            const selectedCategoriesInThisIndustry = currentCategoryIds.filter(
              id => categoriesFromCurrentIndustry.includes(id)
            );

            // Check if adding this category would exceed the 3-category limit for this industry
            if (selectedCategoriesInThisIndustry.length >= 3) {
              console.warn(`You can only select up to 3 categories for this industry`);
              return;
            }

            // When selecting ANY category, remove all categories from OTHER industries
            // Keep only categories from current industry + add the new category
            const updatedCategories = [...selectedCategoriesInThisIndustry, category_id];
            setFieldValue("category_ids", updatedCategories);
          }
        };

        const canSelectCategory = (category_id: number) => {
          const currentCategoryIds = values?.category_ids ?? [];
          const isCategorySelected = currentCategoryIds.includes(category_id);
          
          if (isCategorySelected) {
            return true; 
          }

          // Get categories that belong to the current selected industry
          const categoriesFromCurrentIndustry = getCategoriesForIndustry(selectedIndustry);
          
          // Get currently selected categories that belong to this industry
          const selectedCategoriesInThisIndustry = currentCategoryIds.filter(
            id => categoriesFromCurrentIndustry.includes(id)
          );

          return selectedCategoriesInThisIndustry.length < 3;
        };

        const handleImageUpload = async (
          event: React.ChangeEvent<HTMLInputElement>
        ) => {
          const file = event.target.files?.[0];
          if (file && file.type.startsWith("image/")) {
            const reader = new FileReader();
            reader.onload = async (e: ProgressEvent<FileReader>) => {
              if (e.target?.result) {
                setImageSrc(e.target.result as string); // store raw image
                setOpenCropper(true);
              }
            };
            reader.readAsDataURL(file);
          } else {
            console.log("Invalid file type selected.");
          }
        };

        return (
          <Form>
            {/* Business  */}
            {/* <SearchAutocomplete
              onSelect={(place: PlaceDetails) => {
                setFieldValue("business", place?.business ?? values?.business);
                setFieldValue("address2", place?.website ?? values?.address2);
                setFieldValue("phone", place?.phoneNumber ?? values?.phone);
                setFieldValue("address1", place?.address ?? values?.address1);
                setFieldValue("city", place?.city ?? values?.city);
                setFieldValue("state", place?.state ?? values?.state);
                setFieldValue("zipcode", place?.zipcode ?? values?.zipcode);
              }}
              initialValue={values?.business}
            /> */}
            <Box>
              <Label label="Business Name" required={true} />
              <Field
                as={TextField}
                name="business"
                fullWidth
                onChange={handleChange}
                error={
                  touched.business && Boolean(errors.business)
                }
              />
              {errors.business && touched.business && (
                <Typography color="red" fontSize="12px">
                  {errors.business}
                </Typography>
              )}
              <PostScript text="Hit your top selling points in two lines or less!" />
            </Box>
            {/* Headline */}
            <Box>
              <Label label="Headline" required={true} />
              <Field
              as={TextField}
              name="business_headline"
              fullWidth
              multiline
              maxRows={4}
              onChange={handleChange}
              error={
                touched.business_headline && Boolean(errors.business_headline)
              }
              />
              {errors.business_headline && touched.business_headline && (
              <Typography color="red" fontSize="12px">
                {errors.business_headline}
              </Typography>
              )}
            </Box>

            {/* Photo Section */}
            <Box>
              <Box sx={editBusinessProfileStyles.header}>
                <Label label="Photo" required={true} />
                <label htmlFor="upload-image">
                  <Typography sx={editBusinessProfileStyles.editPhotoText}>
                    Edit Photo
                  </Typography>
                </label>
                <input
                  type="file"
                  id="upload-image"
                  onChange={handleImageUpload}
                  style={{ display: "none" }}
                  accept="image/*"
                />
              </Box>

              <Box>
                {values?.image ? (
                  <img
                    src={`${awsImageRootUrl}/${values?.image}`}
                    style={{
                      height: "230px",
                      width: "100%",
                      borderRadius: "8px",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <img
                    src={BusinessImage}
                    alt="Business"
                    style={{
                      width: "100%",
                      height: "230px",
                      borderRadius: "8px",
                      objectFit: "cover",
                    }}
                  />
                )}
              </Box>
              <canvas
                ref={canvasRef}
                width={500}
                height={500}
                style={{ display: "none" }}
              ></canvas>
            </Box>

            <Dialog
              open={openCropper}
              onClose={() => setOpenCropper(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogContent
                sx={{ position: "relative", height: 400, background: "#333" }}
              >
                {imageSrc && (
                  <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={16 / 9}
                    cropShape="rect"
                    rotation={0}
                    showGrid={true}
                    onCropChange={setCrop}
                    onZoomChange={setZoom}
                    onCropComplete={onCropComplete}
                  />
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setOpenCropper(false)}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={() => handleCropConfirm(setFieldValue)}
                >
                  Crop & Upload
                </Button>
              </DialogActions>
            </Dialog>

            {/* Industries */}
            <Box sx={{ marginTop: "1rem" }}>
              <Label label="Select Industries" required={true} />
              <Box display="flex" flexWrap="wrap" gap={1}>
                {industries &&
                  industries?.map((industryType: Industry) => {
                    const _selected = selectedIndustry === industryType?.id;

                    return (
                      <Chip
                        key={industryType.id}
                        label={industryType.industry}
                        onClick={() =>
                          handleSelectIndustryCategory(industryType.id, setFieldValue)
                        }
                        variant={_selected ? "filled" : "outlined"}
                        color={_selected ? "primary" : "default"}
                        sx={{
                          margin: "0 0.5rem 0.5rem 0",
                          border: _selected ? "none" : "1px solid #000",
                        }}
                      />
                    );
                  })}
              </Box>
              {errors.industry_ids && touched.industry_ids && (
                <Typography color="red" fontSize="12px">
                  {errors.industry_ids}
                </Typography>
              )}
            </Box>

            {/* Categories */}
            <Box sx={{ marginTop: "1rem" }}>
              <Label label="Select Categories" required={true} />
              <Box display="flex" flexWrap="wrap" gap={1}>
                {filteredCategories &&
                  filteredCategories?.map((category: Category) => {
                    const _selected = values?.category_ids?.includes(category?.id);
                    const _canSelect = canSelectCategory(category?.id);

                    return (
                      <Chip
                        key={category.id}
                        label={category.category}
                        onClick={() => handleSelectCategory(category.id)}
                        variant={_selected ? "filled" : "outlined"}
                        color={_selected ? "primary" : "default"}
                        sx={{
                          margin: "0 0.5rem 0.5rem 0",
                          border: _selected ? "none" : "1px solid #000",
                          cursor: "pointer"
                        }}
                      />
                    );
                  })}
              </Box>
              {errors.category_ids && touched.category_ids && (
                <Typography color="red" fontSize="12px">
                  {errors.category_ids}
                </Typography>
              )}
            </Box>

            {/* About */}
            <Box>
              <Label label="About" required={true} />
              <Field
                as={TextField}
                name="business_description"
                fullWidth
                onChange={handleChange}
                error={
                  touched.business_description &&
                  Boolean(errors.business_description)
                }
                multiline
                rows={3}
              />
              {errors.business_description && touched.business_description && (
                <Typography color="red" fontSize="12px">
                  {errors.business_description}
                </Typography>
              )}
              <PostScript text="Go into detail about your business and the items that you are selling" />
            </Box>

            {/* Website */}
            <Box>
              <Label label="Website"  />
              <Field
                as={TextField}
                name="address2"
                fullWidth
                onChange={handleChange}
                error={touched.address2 && Boolean(errors.address2)}
              />
              {errors.address2 && touched.address2 && (
                <Typography color="red" fontSize="12px">
                  {errors.address2}
                </Typography>
              )}
            </Box>

            {/* Phone */}
            <Box>
              <Label label="Phone" required={true} />
              <Field
                as={TextField}
                name="phone"
                fullWidth
                onChange={handleChange}
                error={touched.phone && Boolean(errors.phone)}
              />
              {errors.phone && touched.phone && (
                <Typography color="red" fontSize="12px">
                  {errors.phone}
                </Typography>
              )}
            </Box>

            {/* Address */}
            <Box>
              <Label label="Address"  />
              <Field
                as={TextField}
                name="address1"
                fullWidth
                onChange={handleChange}
                error={touched.address1 && Boolean(errors.address1)}
              />
              {errors.address1 && touched.address1 && (
                <Typography color="red" fontSize="12px">
                  {errors.address1}
                </Typography> 
              )}
            </Box>

            {/* City */}
            <Box>
              <Label label="City" required={true} />
              <Field
                as={TextField}
                name="city"
                fullWidth
                onChange={handleChange}
                error={errors.city }
              />
              {errors.city && (
                <Typography color="red" fontSize="12px">
                  {errors.city}
                </Typography>
              )}
            </Box>

            {/* State */}
            <Box>
              <Label label="State" required={true} />
              <Field
                as={TextField}
                name="state"
                fullWidth
                onChange={handleChange}
                error={touched.state && Boolean(errors.state)}
              />
              {errors.state && touched.state && (
                <Typography color="red" fontSize="12px">
                  {errors.state}
                </Typography>
              )}
            </Box>

            {/* ZipCode */}
            <Box>
              <Label label="ZipCode" required={true} />
              <Field
                as={TextField}
                name="zipcode"
                fullWidth
                onChange={handleChange}
                error={touched.zipcode && Boolean(errors.zipcode)}
              />
              {errors.zipcode && touched.zipcode && (
                <Typography color="red" fontSize="12px">
                  {errors.zipcode}
                </Typography>
              )}
            </Box>

            <Button
              onPointerDown={() => {
                // Hack to ensure Formik registers the button click before losing focus
                console.log("pointer down");
                onSubmit(values);
              }}
              type="submit"
              variant="contained"
              sx={{ width: "100%", marginTop: "20px", paddingY: "10px" }}
              disabled={editLoading}
            >
              {editLoading ? "loading" : "Save"}
            </Button>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EditBusinessForm;